import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import {
  ArrowUturnLeftIcon,
  Bars3Icon,
  BellAlertIcon,
  CalendarDaysIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  TrashIcon,
  UserGroupIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { DatePickerCalendar, DatePickerField } from "@/components/ui/DatePicker";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { searchEmployees } from "@/lib/projectOverview";
import { toast } from "@/lib/toast";
import {
  createTodo,
  deleteTodo,
  fetchTodos,
  formatRemindDate,
  reorderTodos,
  setTodoDone,
  todayIsoDate,
  todoDueState,
  updateTodo,
  type TodoDueState,
  type TodoItem,
  type TodoPerson,
} from "@/lib/todos";
import { useRealtime } from "@/lib/useRealtime";
import { NoteComposer } from "@/projectOverview/notes/NoteComposer";
import {
  resolveReferencePositions,
  splitNoteBodySegments,
} from "@/projectOverview/notes/noteReferences";
import { useNoteReferenceAction } from "@/projectOverview/notes/useNoteReferenceAction";
import type { DraftNoteReference } from "@/projectOverview/notes/types";

/**
 * To-Dos & Wiedervorlage für alle Mitarbeiter – prominenter Bereich auf der
 * Home-Seite. Unterstützt Erwähnungen/Verknüpfungen wie in Projekt-Notizen,
 * Wiedervorlage-Daten mit auffälliger Fälligkeits-Markierung, Priorisierung
 * per Drag & Drop sowie Übergabe an / Einladen von Mitarbeitern.
 */
export function TodoPanel() {
  const { handleRefClick, fallbackModal } = useNoteReferenceAction(0);

  const [todos, setTodos] = useState<TodoItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [newRemindAt, setNewRemindAt] = useState("");
  const [showDone, setShowDone] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [peopleDialog, setPeopleDialog] = useState<{
    todo: TodoItem;
    mode: "assign" | "invite";
  } | null>(null);
  const [dragId, setDragId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    try {
      setError(null);
      setTodos(await fetchTodos());
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setForbidden(true);
        return;
      }
      setError("To-Dos konnten nicht geladen werden.");
      setTodos((prev) => prev ?? []);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  useRealtime({
    enabled: !forbidden,
    scopes: ["domain:todos", "todos:list"],
    domains: ["todos"],
    events: ["todos_changed"],
    onChange: reload,
  });

  const openTodos = useMemo(
    () =>
      (todos ?? [])
        .filter((t) => !t.done_at)
        .sort((a, b) => a.position - b.position || b.id - a.id),
    [todos],
  );
  const dueTodos = useMemo(
    () =>
      openTodos
        .filter((t) => {
          const state = todoDueState(t);
          return state === "overdue" || state === "due-today";
        })
        .sort((a, b) => String(a.remind_at).localeCompare(String(b.remind_at))),
    [openTodos],
  );
  const dueIds = useMemo(() => new Set(dueTodos.map((t) => t.id)), [dueTodos]);
  const listTodos = useMemo(
    () => openTodos.filter((t) => !dueIds.has(t.id)),
    [openTodos, dueIds],
  );
  const doneTodos = useMemo(
    () =>
      (todos ?? [])
        .filter((t) => !!t.done_at)
        .sort(
          (a, b) =>
            (Date.parse(String(b.done_at)) || 0) - (Date.parse(String(a.done_at)) || 0),
        ),
    [todos],
  );

  const applyItem = useCallback((item: TodoItem) => {
    setTodos((prev) =>
      prev ? prev.map((t) => (t.id === item.id ? item : t)) : prev,
    );
  }, []);

  const handleCreate = useCallback(
    async (payload: { note: string; references: DraftNoteReference[] }) => {
      setSaving(true);
      setComposerError(null);
      try {
        const created = await createTodo({
          text: payload.note,
          references: payload.references,
          remind_at: newRemindAt || null,
        });
        setNewRemindAt("");
        setTodos((prev) => (prev ? [created, ...prev] : [created]));
      } catch {
        setComposerError("To-Do konnte nicht gespeichert werden.");
      } finally {
        setSaving(false);
      }
    },
    [newRemindAt],
  );

  const handleToggleDone = useCallback(
    async (todo: TodoItem, done: boolean) => {
      setTodos((prev) =>
        prev
          ? prev.map((t) =>
              t.id === todo.id
                ? { ...t, done_at: done ? new Date().toISOString() : null }
                : t,
            )
          : prev,
      );
      try {
        applyItem(await setTodoDone(todo.id, done));
      } catch {
        toast.error("Status konnte nicht gespeichert werden.");
        void reload();
      }
    },
    [applyItem, reload],
  );

  const handleSaveEdit = useCallback(
    async (todo: TodoItem, payload: { note: string; references: DraftNoteReference[] }) => {
      setEditingId(null);
      try {
        applyItem(
          await updateTodo(todo.id, {
            text: payload.note,
            references: payload.references,
          }),
        );
      } catch {
        toast.error("Änderung konnte nicht gespeichert werden.");
        void reload();
      }
    },
    [applyItem, reload],
  );

  const handleRemindChange = useCallback(
    async (todo: TodoItem, value: string | null) => {
      setTodos((prev) =>
        prev
          ? prev.map((t) => (t.id === todo.id ? { ...t, remind_at: value } : t))
          : prev,
      );
      try {
        applyItem(await updateTodo(todo.id, { remind_at: value }));
      } catch {
        toast.error("Wiedervorlage konnte nicht gespeichert werden.");
        void reload();
      }
    },
    [applyItem, reload],
  );

  const handleAssign = useCallback(
    async (todo: TodoItem, person: TodoPerson | null) => {
      try {
        applyItem(await updateTodo(todo.id, { assigned: person }));
        toast.success(
          person ? `To-Do an ${person.display} übergeben.` : "Übergabe entfernt.",
        );
      } catch {
        toast.error("Übergabe konnte nicht gespeichert werden.");
        void reload();
      }
    },
    [applyItem, reload],
  );

  const handleInvite = useCallback(
    async (todo: TodoItem, persons: TodoPerson[]) => {
      try {
        applyItem(await updateTodo(todo.id, { participants: persons }));
      } catch {
        toast.error("Einladungen konnten nicht gespeichert werden.");
        void reload();
      }
    },
    [applyItem, reload],
  );

  const handleDelete = useCallback(
    async (todo: TodoItem) => {
      if (!window.confirm("Dieses To-Do wirklich löschen?")) return;
      setTodos((prev) => (prev ? prev.filter((t) => t.id !== todo.id) : prev));
      try {
        await deleteTodo(todo.id);
      } catch {
        toast.error("To-Do konnte nicht gelöscht werden.");
        void reload();
      }
    },
    [reload],
  );

  const handleDrop = useCallback(
    (targetId: number) => {
      if (dragId == null || dragId === targetId) {
        setDragId(null);
        setDragOverId(null);
        return;
      }
      const current = listTodos.map((t) => t.id);
      const fromIdx = current.indexOf(dragId);
      const toIdx = current.indexOf(targetId);
      if (fromIdx < 0 || toIdx < 0) {
        setDragId(null);
        setDragOverId(null);
        return;
      }
      const next = [...current];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, dragId);
      // Fällige To-Dos behalten ihren Platz vor der Liste.
      const orderedIds = [...dueTodos.map((t) => t.id), ...next];
      const positionById = new Map(orderedIds.map((id, idx) => [id, idx]));
      setTodos((prev) =>
        prev
          ? prev.map((t) =>
              positionById.has(t.id)
                ? { ...t, position: positionById.get(t.id)! }
                : t,
            )
          : prev,
      );
      setDragId(null);
      setDragOverId(null);
      void reorderTodos(orderedIds).catch(() => {
        toast.error("Sortierung konnte nicht gespeichert werden.");
        void reload();
      });
    },
    [dragId, listTodos, dueTodos, reload],
  );

  if (forbidden) return null;

  const loading = todos === null && !error;
  const openCount = openTodos.length;
  const dueCount = dueTodos.length;

  const rowCommonProps = {
    editingId,
    onStartEdit: (todo: TodoItem) => setEditingId(todo.id),
    onCancelEdit: () => setEditingId(null),
    onSaveEdit: handleSaveEdit,
    onToggleDone: handleToggleDone,
    onRemindChange: handleRemindChange,
    onOpenAssign: (todo: TodoItem) => setPeopleDialog({ todo, mode: "assign" }),
    onOpenInvite: (todo: TodoItem) => setPeopleDialog({ todo, mode: "invite" }),
    onDelete: handleDelete,
    onRefClick: handleRefClick,
  };

  return (
    <Card
      className="eh-todo-panel border-l-4 !border-l-accent"
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          Meine To-Dos &amp; Wiedervorlage
          {openCount > 0 ? (
            <span className="rounded-full bg-accent/15 px-2 py-0.5 text-xs font-medium text-accent">
              {openCount} offen
            </span>
          ) : null}
          {dueCount > 0 ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-status-red px-2 py-0.5 text-xs font-semibold text-white">
              <BellAlertIcon className="h-3.5 w-3.5" aria-hidden />
              {dueCount} fällig
            </span>
          ) : null}
        </span>
      }
      description="@ Mitarbeiter sieht das To-Do ebenfalls – gekennzeichnet als von dir. Verknüpfungen fügst du über die Schaltflächen ein."
      actions={
        <Button
          variant="secondary"
          size="sm"
          type="button"
          disabled={doneTodos.length === 0}
          onClick={() => setShowDone((v) => !v)}
        >
          {showDone ? "Erledigte ausblenden" : `Erledigte (${doneTodos.length})`}
        </Button>
      }
      bodyClassName="!p-4 space-y-5"
    >
      {error ? <Alert variant="error">{error}</Alert> : null}

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <>
          {dueTodos.length > 0 ? (
            <section aria-label="Wiedervorlage fällig" className="space-y-2">
              <h3 className="inline-flex items-center gap-1.5 text-sm font-semibold text-status-red">
                <BellAlertIcon className="h-4 w-4" aria-hidden />
                Wiedervorlage – jetzt dran
              </h3>
              <ul className="eh-todo-list" role="list">
                {dueTodos.map((todo) => (
                  <TodoRow key={todo.id} todo={todo} prominent {...rowCommonProps} />
                ))}
              </ul>
            </section>
          ) : null}

          <div className="eh-todo-compose">
            <NoteComposer
              orderId={0}
              isBuero={false}
              saving={saving}
              error={composerError}
              onSubmit={(payload) =>
                void handleCreate({ note: payload.note, references: payload.references })
              }
              globalReferences
              embedded
              placeholder="Neues To-Do eingeben …"
            />
            <div className="eh-todo-compose__remind">
              <span>Wiedervorlage (optional):</span>
              <DatePickerField
                value={newRemindAt || null}
                onChange={(value) => setNewRemindAt(value ?? "")}
                minDate={todayIsoDate()}
                allowClear
                placeholder="Datum wählen"
                buttonClassName="!h-9 !text-xs"
              />
            </div>
          </div>

          {listTodos.length === 0 && dueTodos.length === 0 ? (
            <p className="text-sm text-label-secondary">
              <em>Keine offenen To-Dos – neues To-Do oben eingeben.</em>
            </p>
          ) : listTodos.length > 0 ? (
            <ul className="eh-todo-list" role="list">
              {listTodos.map((todo) => (
                <TodoRow
                  key={todo.id}
                  todo={todo}
                  draggable={editingId == null}
                  isDragOver={dragOverId === todo.id && dragId !== todo.id}
                  onDragStartRow={() => setDragId(todo.id)}
                  onDragOverRow={() => setDragOverId(todo.id)}
                  onDropRow={() => handleDrop(todo.id)}
                  onDragEndRow={() => {
                    setDragId(null);
                    setDragOverId(null);
                  }}
                  {...rowCommonProps}
                />
              ))}
            </ul>
          ) : null}

          {showDone && doneTodos.length > 0 ? (
            <section aria-label="Erledigte To-Dos" className="space-y-2 border-t border-separator pt-3">
              <h3 className="text-sm font-semibold text-label-secondary">Erledigt</h3>
              <ul className="eh-todo-list" role="list">
                {doneTodos.map((todo) => (
                  <TodoRow key={todo.id} todo={todo} {...rowCommonProps} />
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}

      {peopleDialog ? (
        <EmployeePickerDialog
          open
          mode={peopleDialog.mode}
          todo={peopleDialog.todo}
          onClose={() => setPeopleDialog(null)}
          onAssign={(person) => {
            void handleAssign(peopleDialog.todo, person);
            setPeopleDialog(null);
          }}
          onInvite={(persons) => {
            void handleInvite(peopleDialog.todo, persons);
            setPeopleDialog(null);
          }}
        />
      ) : null}

      {fallbackModal}
    </Card>
  );
}

function TodoRow({
  todo,
  prominent = false,
  draggable = false,
  isDragOver = false,
  editingId,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onToggleDone,
  onRemindChange,
  onOpenAssign,
  onOpenInvite,
  onDelete,
  onRefClick,
  onDragStartRow,
  onDragOverRow,
  onDropRow,
  onDragEndRow,
}: {
  todo: TodoItem;
  prominent?: boolean;
  draggable?: boolean;
  isDragOver?: boolean;
  editingId: number | null;
  onStartEdit: (todo: TodoItem) => void;
  onCancelEdit: () => void;
  onSaveEdit: (
    todo: TodoItem,
    payload: { note: string; references: DraftNoteReference[] },
  ) => void;
  onToggleDone: (todo: TodoItem, done: boolean) => void;
  onRemindChange: (todo: TodoItem, value: string | null) => void;
  onOpenAssign: (todo: TodoItem) => void;
  onOpenInvite: (todo: TodoItem) => void;
  onDelete: (todo: TodoItem) => void;
  onRefClick: (type: string, id: string, label: string) => void;
  onDragStartRow?: () => void;
  onDragOverRow?: () => void;
  onDropRow?: () => void;
  onDragEndRow?: () => void;
}) {
  const done = !!todo.done_at;
  const dueState = todoDueState(todo);
  const isEditing = editingId === todo.id;

  if (isEditing) {
    return (
      <li className="rounded-ios border border-accent/50 bg-fill-quaternary/20 p-3">
        <NoteComposer
          key={`edit-${todo.id}`}
          orderId={0}
          isBuero={false}
          saving={false}
          error={null}
          onSubmit={(payload) =>
            onSaveEdit(todo, { note: payload.note, references: payload.references })
          }
          globalReferences
          embedded
          placeholder="To-Do bearbeiten …"
          initialText={todo.text}
          initialReferences={resolveReferencePositions(todo.text, todo.references || [])}
        />
        <div className="mt-2 flex justify-end">
          <Button variant="secondary" size="sm" type="button" onClick={onCancelEdit}>
            Abbrechen
          </Button>
        </div>
      </li>
    );
  }

  return (
    <li
      className={clsx(
        "eh-todo-row group flex items-start gap-3 rounded-ios border px-3.5 py-3 transition",
        prominent
          ? "border-status-red/60 border-l-4 !border-l-status-red bg-status-red/10"
          : !todo.viewer_is_creator
            ? "border-separator border-l-4 !border-l-accent/50 bg-accent/5"
            : "border-separator bg-fill-quaternary/20",
        done && "opacity-70",
        isDragOver && "ring-2 ring-accent",
      )}
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        onDragStartRow?.();
      }}
      onDragOver={(e) => {
        if (!onDropRow) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        onDragOverRow?.();
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDropRow?.();
      }}
      onDragEnd={onDragEndRow}
    >
      {draggable ? (
        <span
          className="mt-1 cursor-grab text-label-tertiary opacity-60 group-hover:opacity-100"
          title="Zum Sortieren ziehen"
          aria-hidden
        >
          <Bars3Icon className="h-4 w-4" />
        </span>
      ) : null}

      <button
        type="button"
        role="checkbox"
        aria-checked={done}
        aria-label={done ? "Als offen markieren" : "Als erledigt markieren"}
        onClick={() => onToggleDone(todo, !done)}
        className={clsx(
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition",
          done
            ? "border-status-green bg-status-green text-white"
            : "border-separator-opaque bg-card hover:border-accent",
        )}
      >
        {done ? (
          <svg viewBox="0 0 20 20" className="h-3 w-3" fill="currentColor" aria-hidden>
            <path
              fillRule="evenodd"
              d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0Z"
              clipRule="evenodd"
            />
          </svg>
        ) : null}
      </button>

      <div className="min-w-0 flex-1">
        <div
          className={clsx(
            "text-sm leading-6 text-label",
            done && "line-through text-label-secondary",
          )}
        >
          <TodoBody text={todo.text} references={todo.references} onRefClick={onRefClick} />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-label-tertiary">
          {prominent ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-status-red px-2 py-0.5 font-semibold text-white">
              <BellAlertIcon className="h-3.5 w-3.5" aria-hidden />
              {dueState === "overdue"
                ? `Überfällig seit ${formatRemindDate(todo.remind_at)}`
                : "Heute fällig"}
            </span>
          ) : null}
          {!todo.viewer_is_creator ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 font-medium text-accent">
              Von {todo.created_by_display || "jemand anderem"}
              {todo.viewer_is_mentioned ? " · du wurdest erwähnt" : null}
            </span>
          ) : null}
          {todo.assigned_display ? (
            <span className="rounded-full bg-accent/15 px-2 py-0.5 font-medium text-accent">
              → {todo.assigned_display}
            </span>
          ) : null}
          {todo.participants.length > 0 ? (
            <span className="inline-flex items-center gap-1">
              <UserGroupIcon className="h-3.5 w-3.5" aria-hidden />
              {todo.participants.map((p) => p.display).join(", ")}
            </span>
          ) : null}
          {done && todo.done_by_display ? (
            <span>erledigt von {todo.done_by_display}</span>
          ) : null}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {!done ? (
          <RemindChip
            value={todo.remind_at ?? null}
            dueState={dueState}
            onChange={(value) => onRemindChange(todo, value)}
          />
        ) : null}

        <Menu as="div" className="relative">
          <MenuButton
            className="flex h-8 w-8 items-center justify-center rounded-full text-label-secondary hover:bg-fill-tertiary"
            aria-label="Aktionen"
          >
            <EllipsisVerticalIcon className="h-5 w-5" aria-hidden />
          </MenuButton>
          <MenuItems className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-ios border border-separator bg-card py-1 shadow-ios-md focus:outline-none">
            {!done ? (
              <>
                <TodoMenuItem
                  icon={<PencilSquareIcon className="h-4 w-4" aria-hidden />}
                  label="Bearbeiten"
                  onClick={() => onStartEdit(todo)}
                />
                <TodoMenuItem
                  icon={<UserPlusIcon className="h-4 w-4" aria-hidden />}
                  label="An Mitarbeiter übergeben …"
                  onClick={() => onOpenAssign(todo)}
                />
                <TodoMenuItem
                  icon={<UserGroupIcon className="h-4 w-4" aria-hidden />}
                  label="Mitarbeiter einladen …"
                  onClick={() => onOpenInvite(todo)}
                />
              </>
            ) : (
              <TodoMenuItem
                icon={<ArrowUturnLeftIcon className="h-4 w-4" aria-hidden />}
                label="Wieder öffnen"
                onClick={() => onToggleDone(todo, false)}
              />
            )}
            {todo.viewer_is_creator ? (
              <TodoMenuItem
                icon={<TrashIcon className="h-4 w-4" aria-hidden />}
                label="Löschen"
                danger
                onClick={() => onDelete(todo)}
              />
            ) : null}
          </MenuItems>
        </Menu>
      </div>
    </li>
  );
}

function TodoMenuItem({
  icon,
  label,
  danger = false,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  danger?: boolean;
  onClick: () => void;
}) {
  return (
    <MenuItem>
      <button
        type="button"
        onClick={onClick}
        className={clsx(
          "flex w-full items-center gap-2 px-3 py-2 text-left text-sm data-[focus]:bg-fill-tertiary",
          danger ? "text-status-red" : "text-label",
        )}
      >
        {icon}
        {label}
      </button>
    </MenuItem>
  );
}

function TodoBody({
  text,
  references,
  onRefClick,
}: {
  text: string;
  references: TodoItem["references"];
  onRefClick: (type: string, id: string, label: string) => void;
}) {
  const segments = splitNoteBodySegments(String(text || ""), references || []);
  return (
    <>
      {segments.map((seg, idx) => {
        if (seg.kind === "ref" && seg.ref) {
          return (
            <button
              key={`ref-${idx}-${seg.ref.start}`}
              type="button"
              className="eh-home-mention-ref"
              onClick={(e) => {
                e.stopPropagation();
                onRefClick(seg.ref!.type, seg.ref!.ref_id, seg.ref!.label || seg.text);
              }}
            >
              {seg.text}
            </button>
          );
        }
        return (
          <span key={`t-${idx}`} className="whitespace-pre-wrap">
            {seg.text}
          </span>
        );
      })}
    </>
  );
}

/** Datum-Chip mit Popover zum Setzen/Ändern/Entfernen der Wiedervorlage. */
function RemindChip({
  value,
  dueState,
  onChange,
}: {
  value: string | null;
  dueState: TodoDueState;
  onChange: (value: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const label = value ? formatRemindDate(value) : null;

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={value ? `Wiedervorlage: ${label}` : "Wiedervorlage setzen"}
        className={clsx(
          "inline-flex h-8 items-center gap-1 rounded-full px-2.5 text-xs font-medium transition",
          dueState === "overdue" || dueState === "due-today"
            ? "bg-status-red text-white hover:opacity-90"
            : value
              ? "bg-fill text-label-secondary hover:bg-fill-tertiary"
              : "text-label-tertiary hover:bg-fill-tertiary hover:text-label",
        )}
      >
        <CalendarDaysIcon className="h-4 w-4" aria-hidden />
        {label ? <span className="hidden sm:inline">{label}</span> : null}
      </button>
      {open ? (
        <div className="absolute right-0 z-20 mt-1 rounded-ios border border-separator bg-card p-3 shadow-ios-md">
          <p className="mb-2 text-xs font-medium text-label">Wiedervorlage</p>
          <DatePickerCalendar
            value={value}
            minDate={todayIsoDate()}
            onSelect={(iso) => {
              setOpen(false);
              onChange(iso);
            }}
          />
          {value ? (
            <div className="mt-2 border-t border-separator pt-2 text-right">
              <button
                type="button"
                className="text-xs text-status-red underline"
                onClick={() => {
                  setOpen(false);
                  onChange(null);
                }}
              >
                Wiedervorlage entfernen
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Mitarbeiter auswählen: Übergabe (einzeln) oder Einladen (mehrere). */
function EmployeePickerDialog({
  open,
  mode,
  todo,
  onClose,
  onAssign,
  onInvite,
}: {
  open: boolean;
  mode: "assign" | "invite";
  todo: TodoItem;
  onClose: () => void;
  onAssign: (person: TodoPerson | null) => void;
  onInvite: (persons: TodoPerson[]) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TodoPerson[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Map<string, TodoPerson>>(
    () => new Map(todo.participants.map((p) => [p.login.toLowerCase(), p])),
  );
  const seqRef = useRef(0);

  useEffect(() => {
    const requestId = ++seqRef.current;
    setLoading(true);
    const timer = window.setTimeout(() => {
      searchEmployees(query, 50)
        .then((rows) => {
          if (requestId !== seqRef.current) return;
          setResults(
            (Array.isArray(rows) ? rows : [])
              .map((row) => ({
                login: String(row.id ?? row.email ?? ""),
                display: String(row.label ?? row.name ?? ""),
              }))
              .filter((p) => p.login && p.display),
          );
        })
        .catch(() => {
          if (requestId === seqRef.current) setResults([]);
        })
        .finally(() => {
          if (requestId === seqRef.current) setLoading(false);
        });
    }, 200);
    return () => window.clearTimeout(timer);
  }, [query]);

  const toggleSelected = (person: TodoPerson) => {
    setSelected((prev) => {
      const next = new Map(prev);
      const key = person.login.toLowerCase();
      if (next.has(key)) next.delete(key);
      else next.set(key, person);
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={mode === "assign" ? "To-Do übergeben" : "Mitarbeiter einladen"}
      description={
        mode === "assign"
          ? "Das To-Do erscheint prominent bei der ausgewählten Person."
          : "Eingeladene sehen und bearbeiten dieses To-Do ebenfalls."
      }
      footer={
        mode === "invite" ? (
          <>
            <Button variant="secondary" size="sm" type="button" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              size="sm"
              type="button"
              onClick={() => onInvite(Array.from(selected.values()))}
            >
              Speichern
            </Button>
          </>
        ) : todo.assigned_login || todo.assigned_display ? (
          <Button
            variant="secondary"
            size="sm"
            type="button"
            onClick={() => onAssign(null)}
          >
            Übergabe entfernen
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-3">
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Mitarbeiter suchen …"
          className="w-full rounded-md border border-separator bg-card px-3 py-2 text-sm text-label"
        />
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {loading ? (
            <p className="py-2 text-center text-sm text-label-secondary">Suche …</p>
          ) : results.length === 0 ? (
            <p className="py-2 text-center text-sm text-label-secondary">Keine Treffer</p>
          ) : (
            results.map((person) => {
              const key = person.login.toLowerCase();
              const isSelected = selected.has(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() =>
                    mode === "assign" ? onAssign(person) : toggleSelected(person)
                  }
                  className={clsx(
                    "flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-fill-tertiary",
                    mode === "invite" && isSelected && "bg-accent/10 text-accent",
                  )}
                >
                  <span className="truncate">{person.display}</span>
                  {mode === "invite" && isSelected ? (
                    <span className="text-xs font-medium">eingeladen</span>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </div>
    </Dialog>
  );
}
